// src/components/ExpenseSection.js (Corrected Refresh Logic)

import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../App';
import { collection, query, where, orderBy, getDocs, doc, addDoc, Timestamp, deleteDoc, limit, startAfter, endBefore, limitToLast } from 'firebase/firestore';
import { toast } from 'react-toastify';
import ConfirmationModal from './Common/ConfirmationModal';
import { TableEmptyRow, TableLoadingRow } from './Common/PanelState';

function ExpenseSection() {
    const { db, user } = useFirebase();
    const [expenses, setExpenses] = useState([]);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [category, setCategory] = useState('Miscellaneous'); 
    const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    
    const [monthlyTotal, setMonthlyTotal] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [lastVisible, setLastVisible] = useState(null);
    const [firstVisible, setFirstVisible] = useState(null);

    const expenseCategories = ['Miscellaneous', 'Utilities', 'Marketing', 'Rent', 'Office Supplies', 'Salaries'];

    const fetchExpensesAndTotals = useCallback(async (direction = 'first') => {
        if (!db) { setExpenses([]); setIsLoading(false); return; }
        setIsLoading(true);

        const startOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth - 1, 1));
        const endOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth, 0, 23, 59, 59));
        
        const baseQuery = query(collection(db, 'expenses'), where('date', '>=', startOfMonth), where('date', '<=', endOfMonth));

        try {
            // Calculate totals only when loading the first page or filters change
            if (direction === 'first') {
                const totalSnap = await getDocs(baseQuery);
                setTotalExpenses(totalSnap.size);
                const total = totalSnap.docs.reduce((sum, doc) => sum + doc.data().amount, 0);
                setMonthlyTotal(total);
            }

            let finalQuery;
            if (direction === 'next' && lastVisible) {
                finalQuery = query(baseQuery, orderBy('date', 'desc'), startAfter(lastVisible), limit(pageSize));
            } else if (direction === 'prev' && firstVisible) {
                finalQuery = query(baseQuery, orderBy('date', 'desc'), endBefore(firstVisible), limitToLast(pageSize));
            } else {
                finalQuery = query(baseQuery, orderBy('date', 'desc'), limit(pageSize));
            }
        
            const docSnap = await getDocs(finalQuery);
            setExpenses(docSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            if (!docSnap.empty) {
                setFirstVisible(docSnap.docs[0]);
                setLastVisible(docSnap.docs[docSnap.docs.length - 1]);
            } else { setFirstVisible(null); setLastVisible(null); }
        } catch (err) { toast.error("Error fetching expenses."); } 
        finally { setIsLoading(false); }
    }, [db, filterYear, filterMonth, pageSize, lastVisible, firstVisible]);

    useEffect(() => {
        setCurrentPage(1);
        setLastVisible(null);
        setFirstVisible(null);
        fetchExpensesAndTotals('first');
    // We only want this to run when filters change
    // eslint-disable-next-line
    }, [filterYear, filterMonth]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description || !amount || !expenseDate) { toast.error("Please fill all required fields."); return; }
        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'expenses'), { description, amount: parseFloat(amount), category, date: Timestamp.fromDate(new Date(expenseDate)), createdAt: Timestamp.now() });
            toast.success("Expense added successfully.");
            setDescription(''); setAmount(''); setCategory('Miscellaneous');
            // --- CORRECTED REFRESH LOGIC ---
            fetchExpensesAndTotals('first'); // Re-fetch totals and first page
        } catch (e) { toast.error(`Error adding expense: ${e.message}`); } 
        finally { setIsSubmitting(false); }
    };
    
    const handleDelete = async () => {
        if (!deletingId) return;
        setIsLoading(true); // Show loading while deleting and refreshing
        try {
            await deleteDoc(doc(db, 'expenses', deletingId));
            toast.success("Expense deleted successfully.");
            // --- CORRECTED REFRESH LOGIC ---
            fetchExpensesAndTotals('first'); // Re-fetch totals and first page
        } catch (e) { toast.error(`Error deleting expense: ${e.message}`); } 
        finally { setDeletingId(null); }
    };
    
    const handleNextPage = () => { if (lastVisible) { setCurrentPage(p => p + 1); fetchExpensesAndTotals('next'); } };
    const handlePrevPage = () => { if (currentPage > 1) { setCurrentPage(p => p - 1); fetchExpensesAndTotals('prev'); } };
    const pageStart = totalExpenses > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalExpenses);

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingId} onClose={() => setDeletingId(null)} onConfirm={handleDelete} title="Delete Expense">Are you sure you want to delete this expense? This action is irreversible.</ConfirmationModal>
            <div className="mb-8"><h1 className="admin-page-title">Expense Management</h1><p className="admin-page-sub">Track and manage all your operational expenses.</p></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1 bg-white p-8 rounded-xl shadow-md">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">New Expense Entry</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div><label className="block text-sm font-medium text-gray-700">Description</label><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Office Lunch" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-sm font-medium text-gray-700">Amount</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="PKR" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Date</label><input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border">
                                {expenseCategories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                            </select>
                        </div>
                        <button type="submit" disabled={isSubmitting} className="w-full text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-transform hover:scale-105" style={{backgroundColor: isSubmitting ? '#a11217' : '#c4151c'}}>
                            {isSubmitting ? "Saving..." : "Add Expense"}
                        </button>
                    </form>
                </div>
                <div className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b flex flex-wrap justify-between items-center gap-4">
                        <h2 className="text-2xl font-bold text-gray-800">Expense History</h2>
                        <div className="flex gap-4">
                            <select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select>
                            <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="px-6 py-3 text-left">Date</th><th className="px-6 py-3 text-left">Details</th><th className="px-6 py-3 text-right">Amount</th>{user.role === 'admin' && <th className="px-6 py-3 text-center">Actions</th>}</tr></thead>
                            <tbody className="divide-y divide-gray-200">
                                {isLoading ? (<TableLoadingRow colSpan={4} label="Loading expenses" />) 
                                : expenses.length > 0 ? expenses.map(exp => (
                                    <tr key={exp.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{new Date(exp.date.toDate()).toLocaleDateString()}</td>
                                        <td className="px-6 py-4"><div className="font-semibold text-gray-800">{exp.description}</div><div className="text-xs text-gray-500">{exp.category}</div></td>
                                        <td className="px-6 py-4 text-right font-bold text-red-600">PKR {exp.amount.toLocaleString()}</td>
                                        {user.role === 'admin' && (<td className="px-6 py-4 text-center"><button onClick={() => setDeletingId(exp.id)} className="font-semibold text-red-600 hover:text-red-800">Delete</button></td>)}
                                    </tr>
                                )) : (<TableEmptyRow colSpan={4} title="No expenses found" hint="No expenses recorded for this period." />)}
                            </tbody>
                            <tfoot className="bg-gray-100 font-bold"><tr className="text-gray-900"><td colSpan="2" className="px-6 py-4 text-right">Total for this month:</td><td className="px-6 py-4 text-right font-mono text-xl" colSpan={user.role === 'admin' ? 2 : 1}>PKR {monthlyTotal.toLocaleString()}</td></tr></tfoot>
                        </table>
                    </div>
                     {totalExpenses > pageSize && (
                        <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 rounded-b-xl border-t">
                            <span className="text-sm font-medium text-gray-700">Showing {pageStart}-{pageEnd} of {totalExpenses}</span>
                            <div className="flex gap-2"><button onClick={handlePrevPage} disabled={currentPage === 1 || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Previous</button><button onClick={handleNextPage} disabled={!lastVisible || pageEnd >= totalExpenses || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Next</button></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExpenseSection;